import { debugError } from '@utils';

class AddToCartData {
  /**
   * Create add to cart data for the configurator
   * Channels and campaigns are handled at cart level
   * @param {Object} variant
   * @param {Object} sku
   * @param {number} quantity
   * @param {string} configurationId
   * @param {string} previousConfigurationId
   * @param {string} uploadKey
   * @param {string} thumbnail
   * @returns {Object}
   */
  create({
    variant,
    sku,
    quantity = 1,
    configurationId,
    previousConfigurationId,
    uploadKey,
    thumbnail,
    imageEffect,
  }) {
    if (!variant || !sku) return null;

    try {
      // Transform gid to a id
      const id = variant?.id?.split('/').pop();

      // Format the thumbnail
      const thumbnailUrl = this.#formatThumbnailUrl(thumbnail);

      // Get the add-ons
      // Format as SKU's for Liquid and Shopify Functions usage
      // Format as Handles for storefrontAPI and UI Extension usage
      const addOns = this.#getAddOns(sku, variant.addOns);
      // Empty handles are dropped rather than joined: a metaobject with no handle would
      // otherwise contribute an empty segment, and two of them produce a bare ',' — truthy, so
      // it would be emitted as a property the checkout extension cannot resolve.
      const addOnHandles = addOns
        ?.map((item) => item.handle)
        .filter(Boolean)
        .join(',');

      // Get options
      // Format as SKU's for Liquid and Shopify Functions usage
      // Format as Handles for storefrontAPI and UI Extension usage
      const options = this.#getOptions(sku, variant.options);
      const optionSkus = options?.map((item) => item.sku)?.join(',');
      const optionHandles = options?.map((item) => item.handle)?.join(',');

      // NGE-11559: a finishing code the configurator SENT but that matches no metaobject
      // on this variant must still reach `_add_ons`, or the surcharge is never charged.
      //
      // Measured on STIT prod 2026-09-18: Foto su Acrilico+Alluminio carries no
      // `add_on.products` at all, so `#getAddOns` returned null for a cart whose sku was
      // {PM:AAP, F:0600401, MT:'HP'}. The configurator quoted 62,80 EUR and the cart
      // charged 57,90 — the 4,90 mounting kit silently vanished, because `channel-price`
      // in tcg-campaigns prices from `_add_ons` and there was nothing to price.
      //
      // Falling back to `_add_ons` (rather than `_options`) is the money-safe direction and
      // matches the rule tcg-configurator.init.js already documents for the embed path:
      // over-inclusion cannot overcharge, because the transform only charges a code the
      // variant's tier-price JSON actually keys, while under-inclusion is a straight loss.
      // The handle lists stay shorter than the code list, which is honest rather than lossy —
      // an unmatched code has no metaobject and therefore no handle.
      const matchedSkus = new Set([
        ...(addOns || []).map((item) => item.sku?.toUpperCase()),
        ...(options || []).map((item) => item.sku?.toUpperCase()),
      ]);
      const unmatched = this.#getUnmatchedFinishingCodes(sku, matchedSkus);

      const addOnSkus = [...(addOns || []).map((item) => item.sku), ...unmatched].join(',');

      // Calculate the original price per unit
      // Over EXACTLY the codes emitted in `_add_ons` — including the unmatched ones — so the
      // strike and the charge are computed over the same set. A subset understates the saving.
      const originalPricePerUnit = this.#calculateOriginalPricePerUnit(
        variant.original_price,
        addOnSkus ? addOnSkus.split(',') : undefined
      );

      // Create the properties
      const properties = {
        // Picturator import path for this line item. Everything the upload widget
        // configures is a single-image design that lives in Picturator's
        // `configureditems` table, so the importer must resolve it from
        // `_configuration_id`. Stated explicitly rather than left to a default: the
        // importer branches on the value, and a missing field would be ambiguous with
        // universal-configurator items. NGE-10195 / NGE-10122.
        _tcg_source: 'single-configurator',
        _sku: sku._,
        _PM: sku.PM,
        _F: sku.F,
        _upload_key: uploadKey,
        _original_price_per_unit: originalPricePerUnit,
        _configuration_id: configurationId,
        _previous_configuration_id: previousConfigurationId,
        _thumbnail: thumbnailUrl,
        ...(imageEffect && { _image_effect: imageEffect }),
        ...(addOnSkus && {
          _add_ons: addOnSkus,
          ...(addOnHandles && { _add_on_handles: addOnHandles }),
          ...(addOns?.length > 0 && { _add_ons_display: this.#generateDisplayData(addOns) }),
        }),
        ...(options &&
          options?.length > 0 && {
            _options: optionSkus,
            _option_handles: optionHandles,
            _options_display: this.#generateDisplayData(options),
          }),
      };

      // Create the form data
      return {
        attributes: {
          _market: window.theme.market || '',
          _brand_id: window.theme.brandId || '',
          _language: window.theme.language || '',
          // NGE-12307. A cart attribute is scalar, so handing over the raw metafield
          // object coerces it to the literal "[object Object]". LATENT, not live: 25 of 25
          // recent STIT orders carry a well-formed Ruby-style hash, so something else sets
          // this attribute on the real purchase path — see the note in
          // tests/picturator-preselect.test.mjs. Serialise anyway; the value is a
          // cross-repo contract read by tcg-campaigns' checkout/account price extensions,
          // and standard JSON parses cleanly in their `=>`-replacing parser.
          // The optional chain guards `market_group` being absent — it cannot throw today
          // (html-bottom-js.liquid emits the key unconditionally, `null` when unset), but
          // the shape is not this file's to rely on.
          ...(window.theme.market_group?.index
            ? { _market_groups: JSON.stringify(window.theme.market_group.index) }
            : {}),
        },
        items: [
          {
            id,
            quantity,
            properties,
          },
        ],
      };
    } catch (error) {
      debugError('Error creating add to cart data', error);
      throw new Error(window.toast_translations.cart.no_product);
    }
  }

  /**
   * Format the thumbnail URL
   * @param {string} thumbnail - The thumbnail URL
   * @returns {string} - The formatted thumbnail URL
   */
  #formatThumbnailUrl(thumbnail) {
    if (!thumbnail) return null;
    if (thumbnail.startsWith('data:')) return null;

    // Remove the size parameter from the thumbnail URL
    const thumbnailUrl = new URL(thumbnail);
    thumbnailUrl.searchParams.delete('size');
    return thumbnailUrl.toString();
  }

  /**
   * Get the add-ons
   * @param {Object} sku - The sku object from the configurator
   * @param {Object} addOns - The add-ons of the variant
   * @returns {Object} - The add-ons
   */
  #getAddOns(sku, addOns) {
    if (!sku || !addOns) return null;

    // Transform all sku keys and values to uppercase
    const skuData = Object.fromEntries(
      Object.entries(sku).map(([key, value]) => [key.toUpperCase(), value?.toUpperCase()])
    );

    // Join the SKU array
    const items = addOns.filter((item) => {
      const sku = item.sku?.toUpperCase();
      const type = item.type?.toUpperCase();
      if (sku && type && skuData[type] && skuData[type] === sku) {
        return true;
      }
      return false;
    });

    // Return the add-ons
    return items;
  }

  /**
   * Finishing codes the configurator sent that matched no metaobject on this variant.
   *
   * NGE-11559. The sku object is keyed by finishing TYPE (`MT`, `BM`, `FS`, `FN`, `BE`, `B`)
   * plus the three base keys, which are the product itself and never a finishing choice:
   *   `_`  the full configuration string
   *   `PM` print material
   *   `F`  format
   *
   * Everything else is a customer selection that carries a price, so it must survive into
   * `_add_ons` whether or not this shop happens to have a metaobject for it.
   *
   * @param {Object} sku - The sku object from the configurator
   * @param {Set<string>} matchedSkus - upper-cased skus already emitted as add-ons or options
   * @returns {string[]} the unmatched codes, in sku-key order, de-duplicated
   */
  #getUnmatchedFinishingCodes(sku, matchedSkus) {
    if (!sku) return [];
    const BASE_KEYS = new Set(['_', 'PM', 'F']);
    const out = [];
    const seen = new Set();

    for (const [key, value] of Object.entries(sku)) {
      if (BASE_KEYS.has(key.toUpperCase())) continue;
      if (typeof value !== 'string') continue;
      // Upper-cased and trimmed: the canonical form this contract is written in. tcg-campaigns
      // builds its product-scoped price key the same way (`String(addonSku).trim().toUpperCase()`,
      // lib/util/picturator/productPriceKey.ts), and the UC bridge emits the same
      // (docs/picturator/uc-order-payload-contract.md).
      //
      // Every consumer measured also upper-cases before comparing — `channel-price` folds the
      // whole attribute at extensions/channel-price/src/run.rs:92, `volume-discount` folds both
      // sides of its `ao` filter, and the Liquid side uses `| upcase | strip`. So normalising
      // here is belt-and-braces rather than load-bearing; it exists so the property READS as the
      // canonical code, not because a consumer would otherwise miss it.
      const code = value.trim().toUpperCase();
      // De-duplicate: the editor can legitimately carry one code under two keys, and
      // `channel-price` sums every `_add_ons` entry, so a repeat charges twice.
      if (!code || matchedSkus.has(code) || seen.has(code)) continue;
      seen.add(code);
      out.push(code);
    }

    return out;
  }

  /**
   * Get the options
   * @param {Object} sku - The sku object from the configurator
   * @param {Object} options - The options of the variant
   * @returns {Object} - The options
   */
  #getOptions(sku, options) {
    if (!sku || !options) return null;

    // Transform all sku keys and values to uppercase
    const skuData = Object.fromEntries(
      Object.entries(sku).map(([key, value]) => [key.toUpperCase(), value?.toUpperCase()])
    );

    const items = options.filter((item) => {
      const sku = item.sku?.toUpperCase();
      const type = item.type?.toUpperCase();
      if (sku && type && skuData[type] && skuData[type] === sku) {
        return true;
      }
      return false;
    });

    // Return the options
    return items;
  }

  /**
   * Generate display data for add-ons/options - stores type and title for email templates
   * @param {Array} items - The add-ons or options array
   * @returns {string} - Formatted string like "BM:Canvas Backing|MT:Wall Mount"
   */
  #generateDisplayData(items) {
    if (!items || items.length === 0) return '';

    return items
      .map(
        (item) =>
          // Store type and display_title as structured data
          `${item.type}:${item.display_title}`
      )
      .join('|'); // Use | as separator between items, : as separator within items
  }

  /**
   * Calculate the price per unit
   * @param {number} price - The price of the variant
   * @param {number} compareAtPrice - The compare at price of the variant
   * @param {number} originalPrice - The original price of the variant
   * @param {Array} addOns - The add-on sku's of the variant
   * @returns {number} - The price per unit
   */

  /**
   * Calculate the original price per unit
   * @param {number} priceData - The original price json data of the variant
   * @param {Array} addOns - The add-on sku's of the variant
   * @returns {Array} - The original price per unit
   */
  #calculateOriginalPricePerUnit(priceData, addOns) {
    // Use the default or compare at price from Shopify
    const originalPricePerUnit = [];

    // Get all market codes from the market group index
    const marketGroupIndex = window.theme.market_group?.index;

    // Check if we have a market group index
    if (!marketGroupIndex) {
      return originalPricePerUnit;
    }

    // Check if we have a price data
    if (!priceData || !priceData._) {
      return originalPricePerUnit;
    }

    // Calculate price for each market
    Object.keys(marketGroupIndex).forEach((group) => {
      // Skip the default market
      if (group === '_v') return;
      if (group === '_p') return;

      const marketCode = group.toUpperCase();
      const marketCodeDowncase = marketCode.toLowerCase();
      let marketPrice = priceData._[marketCode] || priceData._[marketCodeDowncase] || 0;

      // Add prices for add-ons
      if (addOns && addOns?.length > 0) {
        addOns.forEach((addOn) => {
          // Case-insensitive on the ADD-ON key, not just the market code. The strike map is
          // authored data and the metaobject `sku` is authored separately, so their casing
          // is not guaranteed to agree: an exact-key lookup silently dropped an add-on's
          // strike whenever they differed, and the line understated its saving. The UC
          // bridge (tcg-configurator.init.js, computeOriginalPricePerUnit) already resolves
          // all three spellings — both paths feed the SAME checkout extension, so a canvas
          // line and a UC line in one order summary must not disagree (@davis-abols,
          // PR #190). NGE-11736.
          const entry =
            priceData[addOn] ||
            priceData[String(addOn).toUpperCase()] ||
            priceData[String(addOn).toLowerCase()];
          if (entry) {
            marketPrice += entry[marketCode] || entry[marketCodeDowncase] || 0;
          }
        });
      }

      // Add the price to the array
      originalPricePerUnit.push({
        mg: marketCode,
        p: marketPrice,
      });
    });

    // Return the original price per unit
    return originalPricePerUnit;
  }
}

const addToCartData = new AddToCartData();
export { addToCartData };
